import { useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import { v4 as uuidv4 } from "uuid";

import { getInitial } from "helpers/getInitial";
import cn from "lib/cn";

export type ImageProps = {
  imageUrl?: string;
  backgroundColor?: string;
  className?: string;
  alt?: string;
  width?: number;
  height?: number;
  label?: React.ReactNode;
  borderRadius?: string;
  imgClassName?: string;
  badgeIcon?: React.ReactNode;
  badgeClassName?: string;
  withTooltip?: boolean;
  tooltipText?: string;
  withoutInitial?: boolean;
};

const Image = ({
  imageUrl,
  backgroundColor = "#007AF5",
  className,
  alt,
  width,
  height,
  label,
  borderRadius = "50%",
  imgClassName,
  badgeClassName,
  badgeIcon,
  withTooltip,
  tooltipText,
  withoutInitial = false,
}: ImageProps) => {
  const [isImageError, setIsImageError] = useState(false);

  const tooltipId = useMemo(() => {
    return uuidv4();
  }, []);

  const handleImageError = () => {
    setIsImageError(true);
  };

  return (
    <div
      id={tooltipId}
      data-tooltip-id={tooltipId}
      data-tooltip-content={tooltipText}
      data-tooltip-hidden={!withTooltip}
      className={cn("relative text-center text-white", className)}
      style={{
        backgroundColor,
        borderRadius,
        lineHeight: height ? `${height}px` : undefined,
        width: width ? `${width}px` : undefined,
        height: height ? `${height}px` : undefined,
        minWidth: width ? `${width}px` : undefined,
        minHeight: height ? `${height}px` : undefined,
      }}
    >
      {imageUrl && !isImageError ? (
        <img
          loading="lazy"
          src={imageUrl}
          style={{
            borderRadius,
            width: "100%",
            height: "100%",
          }}
          alt={alt}
          width={width}
          height={height}
          onError={handleImageError}
          className={imgClassName}
        />
      ) : typeof label === "string" && !withoutInitial ? (
        getInitial(label || "")
      ) : (
        label
      )}

      {badgeIcon && (
        <div
          className={cn(
            "absolute bottom-2 right-2 rounded-full p-1",
            badgeClassName
          )}
          style={{
            transform: "translate(50%, 50%)",
          }}
        >
          {badgeIcon}
        </div>
      )}

      {withTooltip && (
        <Tooltip
          id={tooltipId}
          className="z-[999] !text-sm !py-1 !px-2"
          style={{ wordWrap: "break-word" }}
        />
      )}
    </div>
  );
};

export default Image;
